import styled, { x } from '@xstyled/styled-components';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { animated, config, useTransition } from '@react-spring/web';
import { shallow } from 'zustand/shallow';
import { useSequenceDetailsQuery } from '~/data';
import { select } from '~/lib/select';
import { shakeVerticalAnimation } from '~/ui/animation';
import { NotFound } from '~/ui/not-found/NotFound';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import {
  Sequence as SequenceDef,
  TrainingState,
  useTrainingStore
} from '../state';
import { useTrainingParams } from '../useTrainingParams';
import { ComboButton } from './ComboButton';
import { useSequenceStore } from './SequenceStore';
import { translate } from './templates';
import { Root } from './templates/items/root/Root';
import { usePrefetchSequence } from './usePrefetchSequence';
import ChevronIcon from '~/assets/icons/new_chevron.svg';
import { isMobile } from 'react-device-detect';
import { SkipButtons } from '~/training/sequence/SkipButtons';
import { TenSecsBack } from '~/ui/video-player/TenSecsBack';
import { InternetOff } from './InternetOff';
import { ReplayMediaBtn } from '~/ui/video-player/ReplayMediaBtn';
import devtools from 'devtools-detect';
import {
  useContentReady,
  shouldEnableContentReadyTracking
} from '~/lib/useContentReady';

/**
 * Sequence components will be placed on the same grid cell so they can overlap
 * for the transition animation
 */
const SequenceWrapper = animated(styled.div<{ setBackgroundColor: boolean }>`
  grid-row: 1;
  grid-column: 1;
  /* background-color: ${p => p.theme.colors.pinkLight}; */
  background-color: ${p =>
    p.setBackgroundColor ? p.theme.colors.pinkLight : 'transparent'};
`);

const selector = (s: TrainingState) =>
  select(
    s,
    'activateSequence',
    'items',
    'companyName',
    'logoUrl',
    'featureShowLlama',
    'scorePercent'
  );

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset');
interface SequenceComponentProps {
  sequence: SequenceDef;
}

/**
 * Renders the right sequence component based on the type
 */
const SequenceComponent = ({ sequence }: SequenceComponentProps) => {
  const setAutoContinue = useSequenceStore(s => s.setAutoContinue);
  const setPersonalization = useSequenceStore(s => s.setPersonalization);
  const companyName = useTrainingStore(s => s.companyName);
  const logoUrl = useTrainingStore(s => s.logoUrl);
  const featureShowLlama = useTrainingStore(s => s.featureShowLlama);

  const { learnerTrainingId } = useTrainingParams();
  const { data } = useSequenceDetailsQuery({
    variables: {
      learnerTrainingUuid: learnerTrainingId!,
      chapterSequenceId: sequence.chapterSequenceId
    }
  });

  const RootComponent = useMemo(() => {
    return (
      data?.sequence &&
      new Root(
        translate({
          ...data.sequence,
          companyName: companyName,
          logoUrl: logoUrl,
          featureShowLlama: featureShowLlama
        })
      ).component()
    );
  }, [data]);

  useEffect(() => {
    if (!data) return;

    // TODO: change setPersonalization logic
    setPersonalization(data?.sequence?.personalization || false);

    if (data?.sequence?.templateName?.substring(0, 4) === 'Quiz') {
      setAutoContinue(false);
    } else {
      // setAutoContinue(data.sequence.autoContinue || false);
      setAutoContinue(false);
    }
  }, [data]);

  return RootComponent ? <RootComponent key={sequence.id} /> : null;
};

const Chevron = styled(ChevronIcon)`
  fill: ${p => p.theme.colors.accentDark};
  transform: rotate(90deg) scale(2);
  transform-origin: center;
`;

const ScrollIndicator = styled.div`
  position: fixed;
  left: 50%;
  bottom: 80px;
  color: accentDark;
  font-size: 40px;
  font-weight: bold;
  animation: ${shakeVerticalAnimation} 8s ease infinite;
  transform: rotate(90deg);
  pointer-events: none;
`;

export const Sequence = () => {
  const { reset } = useMediaPlayerStore(mediaSelector, shallow);
  const { sequenceId } = useTrainingParams();
  const { activateSequence, items, scorePercent } = useTrainingStore(
    selector,
    shallow
  );

  const newChapter = usePrefetchSequence(sequenceId!);
  const [canScroll, setCanScroll] = useState(false);
  const containerRef = useRef<HTMLElement>();
  const childRef = useRef<HTMLElement>();
  const setRxdInteractions = useSequenceStore(s => s.setRxdInteractions);
  const clearBlocker = useSequenceStore(s => s.clearBlocker);
  const { setPlayer } = useGlobalMediaStore();

  const { learnerTrainingId } = useTrainingParams();

  const shouldTrackContentReady = shouldEnableContentReadyTracking();
  const isContentReady = useContentReady(shouldTrackContentReady);

  useEffect(() => {
    if (isContentReady && shouldTrackContentReady) {
      const mainElement = document.querySelector('main#root');
      if (mainElement) {
        mainElement.classList.add('content-ready');
      }
    }
  }, [isContentReady, shouldTrackContentReady]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isNetworkConnectionSlow, setIsNetworkConnectionSlow] = useState(false);

  useEffect(() => {
    interface RXDInterface {
      SetProgressMeasure?: (value: number) => void;
    }

    let RXD: RXDInterface | undefined;

    try {
      if (window?.document?.referrer) {
        const preUrl = window.document.referrer;
        const domain = preUrl?.split('/')?.[2];

        if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
          RXD = (window.parent as unknown as { RXD?: RXDInterface })?.RXD;
        }
      } else {
        RXD = (window.parent as unknown as { RXD?: RXDInterface })?.RXD;
      }
    } catch (error) {
      // console.error('RXD:', error);
    }

    if (RXD?.SetProgressMeasure) {
      RXD.SetProgressMeasure(Number((+scorePercent / 100).toFixed(2)));
    }
  }, [scorePercent, sequenceId]);

  useEffect(() => {
    interface NetworkInformationConnection extends EventTarget {
      downlink: number;
    }

    let connection: NetworkInformationConnection | undefined;
    let checkNetworkSpeed: (() => void) | undefined;

    if ('connection' in navigator) {
      connection =
        navigator.connection as unknown as NetworkInformationConnection;

      checkNetworkSpeed = () => {
        if (connection!.downlink < 1.35) {
          setIsNetworkConnectionSlow(true);
        } else {
          setIsNetworkConnectionSlow(false);
        }
      };

      checkNetworkSpeed();
      connection.addEventListener('change', checkNetworkSpeed);
    }

    const handlePopState = () => {
      const { player, setPlayer } = useGlobalMediaStore.getState();

      if (player) {
        try {
          player.pause();
        } catch {
          // Player may already be disposed
        }
      }

      setPlayer(undefined);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      if (connection && checkNetworkSpeed) {
        connection.removeEventListener('change', checkNetworkSpeed);
      }
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const checkCanScroll = () => {
    if (isMobile) {
      if (childRef && childRef.current) {
        const val =
          containerRef.current!.clientHeight + containerRef.current!.scrollTop <
          childRef.current!.offsetHeight - 20;
        setCanScroll(val);
      } else {
        setCanScroll(false);
      }
    }
  };

  useEffect(() => {
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [isOnline]);

  useEffect(() => {
    const observer = new ResizeObserver(checkCanScroll);

    containerRef &&
      containerRef.current &&
      observer.observe(containerRef.current!);
    observer &&
      childRef &&
      childRef.current &&
      observer.observe(childRef.current!);

    return () => {
      observer && observer.disconnect();
    };
  }, [containerRef, childRef, activateSequence]);

  const sequence = items.find(
    i => i.kind === 'sequence' && i.id === sequenceId
  ) as SequenceDef;

  const { data, loading, error } =
    sequence && sequence.chapterSequenceId
      ? useSequenceDetailsQuery({
          variables: {
            learnerTrainingUuid: learnerTrainingId!,
            chapterSequenceId: sequence.chapterSequenceId
          }
        })
      : { data: null, loading: false, error: false };

  if (data && data.sequence) {
    setRxdInteractions(
      data?.sequence?.sendRxdInteractions !== null
        ? data?.sequence?.sendRxdInteractions
        : true
    );
  }

  useLayoutEffect(() => {
    if (!sequence) return;

    clearBlocker();
    setPlayer(undefined);
    reset();
    activateSequence(sequence.id);
  }, [sequence]);

  if (!sequence) return <NotFound />;

  // Animates the transition between sequences
  const transitions = useTransition(sequence, {
    from: { clipPath: 'circle(0% at 95% 95%)' },
    enter: newChapter
      ? () => async next => {
          await next({ clipPath: 'circle(70% at 95% 95%)' });
          await next({ clipPath: 'circle(140% at 95% 95%)' });
        }
      : { clipPath: 'circle(140% at 95% 95%)' },
    leave: { clipPath: 'circle(200% at 95% 95%)' },
    config: newChapter ? config.slow : config.molasses
  });

  if (!loading && !!error) return <NotFound btnTitle="Back to my courses" />;

  const addBackgroundColor =
    !!data?.sequence && data.sequence.templateName !== 'Images2BackgroundColor';

  return (
    <x.div
      w="100%"
      h="100%"
      display="grid"
      overflow="auto"
      onScroll={checkCanScroll}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={containerRef as any}
    >
      {isOnline && isNetworkConnectionSlow ? (
        <InternetOff isNetworkConnectionSlow />
      ) : null}
      {!isOnline ? <InternetOff /> : null}
      {transitions((props, item) => (
        <SequenceWrapper
          style={props}
          key={item.id}
          ref={childRef}
          setBackgroundColor={addBackgroundColor}
        >
          {item && <SequenceComponent sequence={item} />}
        </SequenceWrapper>
      ))}
      <ComboButton />
      <TenSecsBack />
      <ReplayMediaBtn />
      <SkipButtons />
      {canScroll && (
        <ScrollIndicator>
          <Chevron />
        </ScrollIndicator>
      )}
      <MediaCaptions />
    </x.div>
  );
};
